/**
 * useDraftPoll
 *
 * Live Draft's polling loop (Draft Board Stage 2.3). Every rule here is
 * from the rebuild prompt directly:
 *   - setTimeout chain, never setInterval — a slow request can't overlap
 *     the next tick, and backoff only has to change one number.
 *   - Paused while the tab is hidden (document.visibilityState), resumed
 *     with one immediate catch-up fetch — never a burst of queued polls.
 *   - Stops entirely once the draft reports status: 'complete'.
 *   - Backoff 10s -> 20s -> 40s -> 60s on error, reset to 10s on success.
 *   - AbortController per request; aborted on unmount or superseded by a
 *     newer request (e.g. the visibility-resume fetch).
 *
 * Terminal state (added after the loop was found spinning forever against
 * a permanent server error): a 4xx other than 429 means the request itself
 * is wrong — a stale 400 for unconfirmed league details, say — and retrying
 * the identical request will never succeed, so that gives up on the first
 * failure. Anything else (429, 5xx, network) gets `maxConsecutiveFailures`
 * tries through the normal backoff before giving up too, in case it clears
 * on its own (except platform errors the backend marks permanent — see
 * PERMANENT_PLATFORM_CODES — which give up at once, like a 4xx) (a 429 tripped by the 10s poll interval usually does once
 * backoff has grown). `failed` is the terminal flag; `retry()` resets it and
 * restarts the loop from a 10s interval.
 *
 * @param {(signal: AbortSignal) => Promise<object>} fetchFn
 * @param {{ intervalMs?: number, maxBackoffMs?: number, maxConsecutiveFailures?: number }} [options]
 * @returns {{ data: object|null, error: Error|null, reconnecting: boolean, failed: boolean, retry: () => void, refresh: () => void }}
 */
import { useEffect, useRef, useState, useCallback } from 'react'

// Platform failures that retrying can never fix — the user has to act (renew
// their ESPN login, add cookies, fix the league ID). The backend forwards the
// platform's own `code`, so these are recognised without string-matching a
// message. Anything else on a 5xx is treated as possibly temporary and retried.
const PERMANENT_PLATFORM_CODES = new Set(['ESPN_AUTH_ERROR', 'ESPN_PRIVATE_LEAGUE', 'ESPN_NOT_FOUND', 'SLEEPER_NOT_FOUND'])

const DEFAULT_INTERVAL_MS = 10000
const DEFAULT_MAX_BACKOFF_MS = 60000
const DEFAULT_MAX_CONSECUTIVE_FAILURES = 3

export function useDraftPoll(fetchFn, {
  intervalMs = DEFAULT_INTERVAL_MS,
  maxBackoffMs = DEFAULT_MAX_BACKOFF_MS,
  maxConsecutiveFailures = DEFAULT_MAX_CONSECUTIVE_FAILURES,
} = {}) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [reconnecting, setReconnecting] = useState(false)
  const [failed, setFailed] = useState(false)
  const [retryToken, setRetryToken] = useState(0)

  const fetchFnRef = useRef(fetchFn)
  useEffect(() => { fetchFnRef.current = fetchFn }, [fetchFn])

  // Lets the page ask for one fetch RIGHT NOW (after the user changes something
  // the next poll would only pick up up to `intervalMs` later). Set by the
  // effect below; a no-op until the loop has started or after it has stopped.
  const pollNowRef = useRef(null)

  useEffect(() => {
    let timer = null
    let abortController = null
    let stopped = false
    let backoff = intervalMs
    let failureCount = 0

    const scheduleNext = (delay) => {
      clearTimeout(timer)
      if (stopped) return
      // Paused while hidden — no timer runs; visibilitychange below fires
      // an immediate poll the moment the tab comes back.
      if (document.visibilityState !== 'visible') return
      timer = setTimeout(poll, delay)
    }

    const giveUp = (err) => {
      stopped = true
      setError(err)
      setReconnecting(false)
      setFailed(true)
    }

    const poll = async () => {
      if (stopped) return
      abortController?.abort()
      const controller = new AbortController()
      abortController = controller

      try {
        const result = await fetchFnRef.current(controller.signal)
        if (controller.signal.aborted || stopped) return

        setData(result)
        setError(null)
        setReconnecting(false)
        failureCount = 0
        backoff = intervalMs // reset on success

        if (result?.status === 'complete') {
          stopped = true
          return
        }
        scheduleNext(backoff)
      } catch (err) {
        if (controller.signal.aborted) return

        const httpStatus = err.response?.status
        const deterministic = (httpStatus >= 400 && httpStatus < 500 && httpStatus !== 429)
          || PERMANENT_PLATFORM_CODES.has(err.response?.data?.code)
        failureCount += 1

        if (deterministic || failureCount >= maxConsecutiveFailures) {
          giveUp(err)
          return
        }

        setError(err)
        setReconnecting(true)
        backoff = Math.min(backoff * 2, maxBackoffMs)
        scheduleNext(backoff)
      }
    }

    pollNowRef.current = () => {
      if (stopped) return
      clearTimeout(timer)
      poll() // the loop reschedules itself from here, same as any other poll
    }

    poll() // initial fetch

    const onVisibility = () => {
      if (stopped) return
      if (document.visibilityState === 'visible') {
        clearTimeout(timer)
        poll() // immediate catch-up — never a queued burst
      } else {
        clearTimeout(timer) // pause
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      stopped = true
      pollNowRef.current = null
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
      abortController?.abort()
    }
  }, [intervalMs, maxBackoffMs, maxConsecutiveFailures, retryToken])

  // Resets the terminal state here rather than at the top of the effect —
  // setting state synchronously in an effect body triggers an extra render
  // for every poll cycle, not just a retry.
  const retry = useCallback(() => {
    setFailed(false)
    setError(null)
    setRetryToken((t) => t + 1)
  }, [])

  const refresh = useCallback(() => { pollNowRef.current?.() }, [])

  return { data, error, reconnecting, failed, retry, refresh }
}
